/**
 * Entity Type API service for managing entity categorization.
 * @module lib/api/entity-types.api
 */

import { apiClient, emptyResponseToNull } from './api-client';
import { createSingletonApi } from './singleton-factory';
import type { EntityType, CreateEntityType, UpdateEntityType } from '@scrambl/shared';

class EntityTypeApiService {
  private baseUrl = '/entity-types';

  /**
   * Gets all entity types for an organization
   */
  async getAll(organizationId: string): Promise<EntityType[]> {
    return apiClient<EntityType[]>(
      `${this.baseUrl}?organizationId=${organizationId}`,
      { method: 'GET' }
    );
  }

  /**
   * Gets visible (non-hidden) entity types for an organization
   */
  async getVisible(organizationId: string): Promise<EntityType[]> {
    return apiClient<EntityType[]>(
      `${this.baseUrl}/visible?organizationId=${organizationId}`,
      { method: 'GET' }
    );
  }

  /**
   * Gets a specific entity type by ID
   */
  async getById(id: string): Promise<EntityType> {
    return apiClient<EntityType>(
      `${this.baseUrl}/${id}`,
      { method: 'GET' }
    );
  }

  /**
   * Gets the default entity type for an organization
   */
  async getDefault(organizationId: string): Promise<EntityType | null> {
    return emptyResponseToNull(
      await apiClient<EntityType | null>(
        `${this.baseUrl}/default?organizationId=${organizationId}`,
        { method: 'GET' }
      ),
    );
  }

  /**
   * Creates a new entity type
   */
  async create(data: CreateEntityType): Promise<EntityType> {
    return apiClient<EntityType>(
      this.baseUrl,
      {
        method: 'POST',
        body: data,
      }
    );
  }

  /**
   * Updates an existing entity type
   */
  async update(id: string, data: UpdateEntityType): Promise<EntityType> {
    return apiClient<EntityType>(
      `${this.baseUrl}/${id}`,
      {
        method: 'PUT',
        body: data,
      }
    );
  }

  /**
   * Soft deletes (hides) an entity type
   */
  async delete(id: string): Promise<void> {
    await apiClient(
      `${this.baseUrl}/${id}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Reorders entity types
   */
  async reorder(typeIds: string[]): Promise<void> {
    await apiClient(
      `${this.baseUrl}/reorder`,
      {
        method: 'PUT',
        body: { typeIds },
      }
    );
  }

  /**
   * Sets an entity type as the default
   */
  async setDefault(id: string): Promise<EntityType> {
    return apiClient<EntityType>(
      `${this.baseUrl}/${id}/default`,
      { method: 'PUT' }
    );
  }

  /**
   * Gets entity counts by type for an organization
   */
  async getEntityCounts(organizationId: string): Promise<Record<string, number>> {
    return apiClient<Record<string, number>>(
      `${this.baseUrl}/counts?organizationId=${organizationId}`,
      { method: 'GET' }
    );
  }
}

export const entityTypeApi = createSingletonApi(EntityTypeApiService);
